import { Link } from 'react-router-dom';
import { openAlertModal } from 'admin/actions/alert-modal';
import {
  bulkSettleClearings,
  getClearingExportBatches,
  getClearingReceivableSummary,
  getClearings,
  updateClearings,
} from 'admin/actions/clearing';
import { hideLoadingSpinner, showLoadingSpinner } from 'admin/actions/loading-spinner';
import { fetchOrganizations } from 'admin/actions/organization';
import { canSettleClearing } from 'admin/authorities';
import Breadcrumb from 'admin/components/shared/Breadcrumb';
import CSVButton from 'admin/components/shared/CSVButton';
import {
  clearingTypeLabel,
  formatDate,
  formatJstFromUtc,
  isBankAccountUnregistered,
  removeEmptyKeys,
  toQueryParams,
} from 'admin/utils';
import moment, { Moment } from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { Button } from 'reactstrap';
import styled from 'styled-components';
import {
  QueryParamConfig,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';
import { components, paths } from '~/javascripts/admin/schema/docs/api/admin';
import getQueryParamDefaultValues from '~/javascripts/admin/utils/use-query-params';
import { useAppDispatch, useAppSelector } from '../../../hooks/useApp';
import { selectUserPrivateMoney } from '../../../reducers/user-private-money';
import Headings from '../../atoms/Headings';
import UserPrivateMoneyNotAllowed from '../../atoms/UserPrivateMoneyNotAllowed';
import SelectedPrivateMoneyDisplay from '../../organisms/SelectedPrivateMoneyDisplay';
import ClearingListTable from './ClearingListTable';
import FilterForm from './FilterForm';
import ZenginExportModal from './ZenginExportModal';

type MemberOrganizations =
  paths['/api/organizations']['get']['responses']['200']['content']['application/json']['rows'];

type Clearings = components['schemas']['Clearing'][];
type ReceivableSummary = components['schemas']['ClearingReceivableSummary'];

type ClearingsQueryParamConfig = typeof queryParamConfigs;
export type ClearingsQueryParams = {
  [key in keyof ClearingsQueryParamConfig]: ClearingsQueryParamConfig[key] extends QueryParamConfig<
    unknown,
    infer T
  >
    ? T
    : never;
};

const PageWrapper = styled.div``;

const SectionWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 0.5em;
`;

const HeadingWrapper = styled.div`
  display: flex;
  align-items: center;

  ${Headings.h5} {
    margin: 0;
  }
`;

const ButtonsWrapper = styled.div`
  display: flex;
  gap: 0.5em;
`;

const BulkBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 8px 0;
  padding: 8px 12px;
  border: 1px solid #d8d8d8;
  border-radius: 4px;
  background: #fafafa;
`;

const BulkCount = styled.span`
  font-size: 13px;
  color: #555;
`;

// 一括精算は1リクエスト最大1000件。超過分はチャンク分割して逐次 POST する（行ロック保持を短く保つ）。
const BULK_SETTLE_CHUNK = 1000;

const UnsettledBatchBanner = styled.div`
  padding: 12px 16px;
  /* 直前が検索フォームのボタン列なので、上にも余白を取って独立した通知に見せる */
  margin: 24px 0 16px;
  border-radius: 4px;
  background: #fff8e1;
  border: 1px solid #ffe082;
  font-size: 13px;
  a {
    margin-left: 8px;
  }
`;

const SummaryBanner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 12px 0;
  padding: 12px 16px;
  border: 1px solid #e0d2b0;
  border-radius: 4px;
  background: #fbf7ec;
`;

// 振込データを作る前に気づけるようにするための注意バナー。
const AttentionBanner = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin: 12px 0;
  padding: 12px 16px;
  border: 1px solid #e6b8b0;
  border-radius: 4px;
  background: #fdf2f0;
  color: #8a3b2c;
  font-size: 13px;
`;

const SummaryHeader = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
`;

const SummaryTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #6b5a20;
`;

const SummaryNote = styled.span`
  font-size: 11px;
  color: #8a7a4a;
`;

const SummaryItems = styled.div`
  display: flex;
  gap: 24px;
  align-items: baseline;
`;

const SummaryItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SummaryLabel = styled.span`
  font-size: 12px;
  color: #8a7a4a;
`;

const SummaryValue = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #6b5a20;
`;

const generateCsvFileName = (isPaymentCSV = false) =>
  isPaymentCSV
    ? `bank_transfer_${moment().format('YYYYMMDDHHmmss')}.csv`
    : `clearing_${moment().format('YYYYMMDDHHmmss')}.csv`;

const csvHeaders = [
  { label: '種別', key: 'type' },
  { label: 'ID', key: 'id' },
  { label: '決済加盟店コード', key: 'claimed_organization_code' },
  { label: '決済加盟店名', key: 'claimed_organization_name' },
  { label: '前回精算日', key: 'from' },
  { label: '締め日', key: 'to' },
  { label: '請求金額合計', key: 'invoice_amount' },
  { label: 'うち消費税額（10％対象）', key: 'consumption_tax_amount' },
  { label: '支払金額', key: 'payment_amount' },
  { label: '精算金額', key: 'total_amount' },
  { label: '債権相殺額', key: 'offset_amount' },
  { label: '予定相殺額', key: 'pending_offset_amount' },
  { label: '実振込額', key: 'actual_transfer_amount' },
  { label: '債権残高', key: 'receivable_balance' },
  { label: '精算状態', key: 'done_at' },
  { label: '発行体名', key: 'issuer_organization_name' },
  { label: '発行体の登録番号', key: 'issuer_invoice_registration_number' },
];

const paymentCSVHeaders = [
  { label: '種別', key: 'type' },
  { label: 'ID', key: 'id' },
  { label: '決済加盟店コード', key: 'claimed_organization_code' },
  { label: '決済加盟店名', key: 'claimed_organization_name' },
  { label: '前回精算日', key: 'from' },
  { label: '締め日', key: 'to' },
  { label: '精算金額', key: 'total_amount' },
  { label: '実振込額', key: 'actual_transfer_amount' },
  { label: '金融機関名', key: 'bank_name' },
  { label: '金融機関コード', key: 'bank_code' },
  { label: '支店名', key: 'bank_branch_name' },
  { label: '支店コード', key: 'bank_branch_code' },
  { label: '口座種別', key: 'bank_account_type' },
  { label: '口座番号', key: 'bank_account' },
  { label: '口座名義人', key: 'bank_account_holder_name' },
  { label: '精算状態', key: 'status' },
  { label: '発行体名', key: 'issuer_organization_name' },
  { label: '発行体の登録番号', key: 'issuer_invoice_registration_number' },
  { label: '請求金額合計', key: 'invoice_amount' },
  { label: 'うち消費税額（10％対象）', key: 'consumption_tax_amount' },
  { label: '支払金額', key: 'payment_amount' },
];

const CRUMBS = [
  { value: 'ホーム', href: '/', active: false },
  { value: '精算管理', href: '', active: true },
  { value: '精算データ一覧', href: '', active: true },
];

const MomentParam: QueryParamConfig<Moment | null | undefined> = {
  encode: (value) => (value ? value.toISOString() : null),
  decode: (value) => (value && typeof value === 'string' ? moment(value) : null),
};

// 未精算バッチ数を数えるために取得する件数（サーバ側の上限）。
const EXPORT_BATCH_FETCH_LIMIT = 200;

const queryParamConfigs = {
  // 精算状態のデフォルトは「未精算」。「全て」は 'all'（明示値）で表現し、送信時に除外する。
  status: withDefault(StringParam, 'pending'),
  claimed_organization_code: withDefault(StringParam, null),
  clearing_type: withDefault(StringParam, null),
  offset_status: withDefault(StringParam, null),
  clearing_start_date_from: withDefault(MomentParam, null),
  clearing_start_date_to: withDefault(MomentParam, null),
  closing_date_from: withDefault(MomentParam, null),
  closing_date_to: withDefault(MomentParam, null),
};

const queryParamDefaultValues = getQueryParamDefaultValues(queryParamConfigs);

const Clearings = () => {
  const user = useAppSelector((state) => state.user);
  const userPrivateMoney = useAppSelector(selectUserPrivateMoney);
  const [clearings, setClearings] = useState<Clearings>([]);
  const [receivableSummary, setReceivableSummary] = useState<ReceivableSummary | null>(null);
  // 未回収債権サマリの基準時刻。「現時点」がいつかを明示するために表示する。
  // サーバは基準時刻を返さないため、取得完了時刻（＝この端末の時計基準の近似値）を用いる。
  const [summaryFetchedAt, setSummaryFetchedAt] = useState<Moment | null>(null);
  const [memberOrganizations, setMemberOrganizations] = useState<MemberOrganizations>([]);
  // 一括精算: 選択中の精算データ id（未精算行のみ選択可）と処理中フラグ。
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);
  // 振込データ作成モーダル（対象マネー選択 → プレビュー → ダウンロード）
  const [isZenginModalOpen, setIsZenginModalOpen] = useState(false);
  // 未精算のまま残っている振込データ。振込したのに精算し忘れているのを検知するため、
  // 一覧を開いた時点で常時可視化する。
  const [unsettledBatchCount, setUnsettledBatchCount] = useState(0);
  const [isUnsettledBatchCountCapped, setIsUnsettledBatchCountCapped] = useState(false);

  const dispatch = useAppDispatch();

  const allowedPrivateMoneys = user.isServiceUser() ? user.issuedMoneys() : user.thirdPartyMoneys();
  const isSelectedMoneyAllowed = allowedPrivateMoneys.some((m) => m.id === userPrivateMoney.id);
  // 精算状態の変更は発行体の管理者のみ（加盟事業者は閲覧のみ）。一括精算 UI は行単位の
  // 「精算済にする」ボタン（ClearingSettleButton）と同一の認可条件でのみ表示する。
  const isIssuer = canSettleClearing(user);

  // 振込先の口座情報が未登録の支払は振込データ(全銀ファイル)から除外される。
  // 従来は振込データを作った瞬間に初めて分かり、締め日直後に加盟店へ問い合わせる時間が
  // なかったため、一覧で平時から気づける状態にする(バナー + 絞り込み + 行内のリンク)。
  const missingBankAccountClearings = clearings.filter(isBankAccountUnregistered);
  const [onlyMissingBankAccount, setOnlyMissingBankAccount] = useState(false);
  const visibleClearings = onlyMissingBankAccount ? missingBankAccountClearings : clearings;

  // 一括精算で選択できるのは未精算行のみ。一覧はサーバページングしていないため clearings = 絞り込み結果の全件。
  // 除外する行:
  //  - 相殺で残高0になった請求（fully_offset）… 対応支払の精算で自動解消されるため手動一括の対象外。
  //  - 予定相殺が付いた請求（pending_offset_amount > 0）… 直接精算すると二重回収になり、
  //    BE も clearing_invoice_has_pending_offset で拒否するため（先に相殺除外が必要）。
  // 表示中の行を対象にする(「口座情報未登録のみ」に絞っているときに、見えていない行が
  // 全選択に含まれないようにするため)。絞り込みなしのときは従来と同じ。
  const selectableClearings = visibleClearings.filter(
    (c) =>
      !c.done_at &&
      c.offset_status !== 'fully_offset' &&
      (c.pending_offset_amount ?? 0) === 0
  );
  const selectableCount = selectableClearings.length;
  const allSelectableSelected =
    selectableCount > 0 && selectableClearings.every((c) => selectedIds.has(c.id));

  const onToggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const onToggleSelectAll = () => {
    setSelectedIds((prev) => {
      const allSelected =
        selectableCount > 0 && selectableClearings.every((c) => prev.has(c.id));
      return allSelected ? new Set() : new Set(selectableClearings.map((c) => c.id));
    });
  };

  const toFilterQueryParams = (query: ClearingsQueryParams) => {
    // 精算状態の「全て」(='all') はサーバへ status を送らない（done/pending のみ有効）。
    const normalized = { ...query, status: query.status === 'all' ? null : query.status };
    return toQueryParams(removeEmptyKeys(normalized));
  };

  const [query, setQuery] = useQueryParams({
    ...queryParamConfigs,
  });

  const clearQuery = () => {
    setQuery({ ...queryParamDefaultValues });
  };

  const handleTaskWithLoader = async ({
    task,
    onSuccess,
    onError,
  }: {
    task: () => Promise<Clearings>;
    onSuccess: (values: Clearings | Clearings[number]) => void;
    onError?: () => void;
  }) => {
    try {
      dispatch(showLoadingSpinner());
      const values = await task();
      if (onSuccess) onSuccess(values);
    } catch (e) {
      dispatch(
        openAlertModal({
          body: 'エラーが発生しました。時間をおいて再度お試し下さい',
          useCancel: false,
        })
      );
      if (onError) onError();
    } finally {
      dispatch(hideLoadingSpinner());
    }
  };

  const fetchClearings = (query: ClearingsQueryParams) => {
    handleTaskWithLoader({
      task: () => getClearings(userPrivateMoney.id, toFilterQueryParams(query)),
      onSuccess: (values: Clearings) => setClearings(values),
      onError: () => setClearings([]),
    });
  };

  const onFinishClearing = (id: string) => {
    handleTaskWithLoader({
      task: () => updateClearings(id),
      onSuccess: (clearing: Clearings[number]) => {
        const newClearings = clearings.map((c) => (c.id === clearing.id ? clearing : c));
        setClearings(newClearings);
      },
    });
  };

  // 種別は clearing_type を優先（金額同額のとき payment/invoice を取り違えないため）。無ければ net で判定。
  const isPaymentClearing = (c: Clearings[number]) =>
    c.clearing_type ? c.clearing_type === 'payment' : (c.payment_amount ?? 0) > (c.invoice_amount ?? 0);

  // 選択 id を 1000 件ずつ逐次 POST。冪等（既done はスキップ）なので途中失敗・離脱しても二重精算は起きない。
  const runBulkSettle = async (ids: string[]) => {
    setIsBulkProcessing(true);
    dispatch(showLoadingSpinner());
    const totals = { settled: 0, cascade_resolved: 0, skipped: 0 };
    let processed = 0;
    let hadError = false;
    let errorMessage: string | null = null;
    try {
      for (let i = 0; i < ids.length; i += BULK_SETTLE_CHUNK) {
        const chunk = ids.slice(i, i + BULK_SETTLE_CHUNK);
        // eslint-disable-next-line no-await-in-loop
        const res = await bulkSettleClearings(chunk);
        totals.settled += res.settled;
        totals.cascade_resolved += res.cascade_resolved;
        totals.skipped += res.skipped;
        processed += chunk.length;
      }
    } catch (e) {
      hadError = true;
      // bulkSettleClearings は !ok 時に response.json()(Promise) を throw するため message を取り出し、
      // 失敗理由（権限・バリデーション等）を結果サマリに添える（運用調査のため握りつぶさない）。
      // eslint-disable-next-line no-await-in-loop
      errorMessage = await Promise.resolve(e)
        .then((b: { message?: string } | null) => (b && typeof b.message === 'string' ? b.message : null))
        .catch(() => null);
    } finally {
      dispatch(hideLoadingSpinner());
      setIsBulkProcessing(false);
    }

    setSelectedIds(new Set());
    fetchClearings(query);

    // BE の bulk-settle は1リクエスト(=1チャンク)を単一トランザクションで all-or-nothing 処理する。
    // 失敗チャンクは全件ロールバック=未処理なので、未処理件数は ids.length - processed で正しい。
    const remaining = ids.length - processed;
    dispatch(
      openAlertModal({
        body: (
          <div>
            <div>
              {`${totals.settled}件を精算済にしました（うち相殺で自動解消 ${totals.cascade_resolved}件 / スキップ済 ${totals.skipped}件）。`}
            </div>
            {hadError && remaining > 0 && (
              <div style={{ marginTop: 8, color: '#a94442' }}>
                {`${remaining}件は未処理です。もう一度「選択を精算済にする」を実行すると続きを処理できます（精算済はスキップされます）。`}
                {errorMessage ? `（理由: ${errorMessage}）` : ''}
              </div>
            )}
          </div>
        ),
        useCancel: false,
      })
    );
  };

  const onClickBulkSettle = () => {
    const selected = clearings.filter((c) => selectedIds.has(c.id) && !c.done_at);
    if (selected.length === 0) return;
    const ids = selected.map((c) => c.id);
    const payments = selected.filter(isPaymentClearing);
    const invoices = selected.filter((c) => !isPaymentClearing(c));
    const transferTotal = payments.reduce((sum, c) => sum + (c.actual_transfer_amount ?? 0), 0);

    dispatch(
      openAlertModal({
        body: (
          <div style={{ textAlign: 'left' }}>
            <p>
              選択した精算データを精算済にします。<strong>この操作は取り消せません。</strong>
            </p>
            <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
              <li>
                対象: {selected.length} 件（支払 {payments.length} 件 / 請求 {invoices.length} 件）
              </li>
              <li>支払の実振込額合計: {transferTotal.toLocaleString()} 円</li>
              <li>対象範囲: {summaryScopeName}（精算状態 = 未精算）</li>
            </ul>
            <p style={{ fontSize: 12, color: '#666' }}>
              ※ 支払を精算済にすると、確定相殺で残高0になった請求も連動して精算済になります。
            </p>
          </div>
        ),
        useConfirm: true,
        useCancel: true,
        confirmText: '精算済にする',
        cancelText: 'キャンセル',
        confirmColor: 'danger',
        onConfirm: () => runBulkSettle(ids),
      })
    );
  };

  const fetchClearingsForCSV = async (isPaymentCSV = false) => {
    const values = await getClearings(userPrivateMoney.id, toFilterQueryParams(query));

    const csvData = values.map((c) => {
      const totalAmount = (c.invoice_amount ?? 0) - (c.payment_amount ?? 0);
      const type = clearingTypeLabel(c);
      return {
        ...c,
        type,
        from: isPaymentCSV
          ? formatDate(formatJstFromUtc(c.from), 'YYYY/MM/DD HH:mm')
          : formatJstFromUtc(c.from),
        to: isPaymentCSV
          ? formatDate(formatJstFromUtc(c.to), 'YYYY/MM/DD HH:mm')
          : formatJstFromUtc(c.to),
        claimed_organization_name: c.claimed_organization?.name,
        claimed_organization_code: c.claimed_organization?.code,
        total_amount: Math.abs(totalAmount),
        done_at: formatJstFromUtc(c.done_at),
        issuer_invoice_registration_number: c.issuer_invoice_registration_number ?? '',
        status: c.done_at ? '精算済' : '未精算',
      };
    });

    if (isPaymentCSV) {
      return csvData.filter((c) => (c.payment_amount ?? 0) > (c.invoice_amount ?? 0));
    }

    return csvData;
  };

  /*
   * A clearing is a summary of all transactions in a period of time between an issuer and a member organization.
   * So, the clearing page is only for private moneys with type 'third-party'
   */

  // 未精算のまま残っている振込データを数える。振込したのに精算し忘れているのを
  // 運用者が気付けるようにするための常時表示。破棄済みは数えない。
  //
  // 一覧 API には未精算だけを数える口が無いため取得した範囲で数える。上限に達したら
  // 「N 件以上」と出す（黙って少なく見せると、警告としての意味が失われる）。
  const fetchUnsettledBatchCount = useCallback(() => {
    if (!isIssuer) return;
    getClearingExportBatches(`limit=${EXPORT_BATCH_FETCH_LIMIT}`)
      .then(({ export_batches: batches }) => {
        const rows = batches ?? [];
        setUnsettledBatchCount(
          rows.filter((batch) => !batch.discarded_at && (batch.unsettled_count ?? 0) > 0).length
        );
        setIsUnsettledBatchCountCapped(rows.length >= EXPORT_BATCH_FETCH_LIMIT);
      })
      .catch(() => setUnsettledBatchCount(0));
  }, [isIssuer]);

  useEffect(() => {
    fetchUnsettledBatchCount();
  }, [fetchUnsettledBatchCount]);

  useEffect(() => {
    const isIssuer = user.organization?.issuer_type?.length;
    // FIXME: fetching member organization options from endpoint maybe should not paginate
    // TODO: this endpoint is not member organization only
    if (isIssuer) {
      fetchOrganizations(`private_money_id=${userPrivateMoney.id}&per_page=10000`).then(
        ({ rows }) => setMemberOrganizations(rows)
      );
    }
  }, [user, userPrivateMoney.id]);

  useEffect(() => {
    if (!isSelectedMoneyAllowed) return undefined;

    // 絞り込みが変わると対象集合が変わるため選択をリセットする。
    setSelectedIds(new Set());
    fetchClearings(query);

    // 未回収債権の集計（一覧のページング非依存。加盟店絞り込みのみ反映）。
    // クエリ高速変更時に古いレスポンスが新しい結果を上書きしないよう世代ガードする。
    let active = true;
    const summaryQuery = query.claimed_organization_code
      ? `claimed_organization_code=${encodeURIComponent(query.claimed_organization_code)}`
      : '';
    getClearingReceivableSummary(userPrivateMoney.id, summaryQuery)
      .then((summary) => {
        if (active) {
          setReceivableSummary(summary);
          setSummaryFetchedAt(moment());
        }
      })
      .catch(() => {
        if (active) {
          setReceivableSummary(null);
          setSummaryFetchedAt(null);
        }
      });

    return () => {
      active = false;
    };
  }, [query, user, userPrivateMoney.id, isSelectedMoneyAllowed]);

  // 一括処理中はタブ閉じ/リロードを警告（送信済みチャンクは精算済で確定済みのため離脱しても整合は保たれる）。
  useEffect(() => {
    if (!isBulkProcessing) return undefined;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isBulkProcessing]);

  // 未回収債権サマリは「現時点の残高（ストック）」。締め日・種別等の絞り込みは反映せず、
  // 加盟店スコープのみ反映する。見出しでスコープを明示して一貫性の欠如を解消する。
  const summaryScopeName = query.claimed_organization_code
    ? memberOrganizations.find((o) => o.code === query.claimed_organization_code)?.name ??
      query.claimed_organization_code
    : '全加盟店';
  // 「現時点」がいつかを明示するため、取得時刻を「YYYY/MM/DD HH:mm 時点」で表示する。
  // 未取得（取得中／取得失敗）時は空にフォールバックする。
  const summaryAsOf = summaryFetchedAt ? `${summaryFetchedAt.format('YYYY/MM/DD HH:mm')} 時点` : '';

  return (
    <PageWrapper>
      <Breadcrumb items={CRUMBS} />
      {isSelectedMoneyAllowed ? (
        <>
          <Headings.h5>検索条件</Headings.h5>
          <SelectedPrivateMoneyDisplay
            highlightHeader={!isSelectedMoneyAllowed}
            allowedPrivateMoneys={allowedPrivateMoneys}
          />
          <FilterForm
            memberOrganizations={memberOrganizations}
            query={query}
            onSearch={setQuery}
            onClear={clearQuery}
          />
          {isIssuer && unsettledBatchCount > 0 && (
            <UnsettledBatchBanner>
              未精算のまま残っている振込データが {unsettledBatchCount} 件
              {isUnsettledBatchCountCapped ? '以上' : ''}あります。銀行へのアップロードが
              済んでいれば精算済にしてください。銀行に上げていないものは破棄できます。
              <Link to="/clearings/export-batches">振込データ履歴を開く</Link>
            </UnsettledBatchBanner>
          )}
          <SummaryBanner>
            <SummaryHeader>
              <SummaryTitle>
                未回収債権（{summaryScopeName}{summaryAsOf ? `・${summaryAsOf}` : ''}）
              </SummaryTitle>
              <SummaryNote>
                ※ {summaryAsOf || '取得時点'}の残高合計です。締め日・種別・相殺状態の絞り込みは反映されません（加盟店のみ反映）
              </SummaryNote>
            </SummaryHeader>
            <SummaryItems>
              <SummaryItem>
                <SummaryLabel>未回収債権残高</SummaryLabel>
                <SummaryValue>
                  {(receivableSummary?.total?.receivable_balance ?? 0).toLocaleString()} 円
                </SummaryValue>
              </SummaryItem>
              <SummaryItem>
                <SummaryLabel>未回収件数</SummaryLabel>
                <SummaryValue>{receivableSummary?.total?.receivable_count ?? 0} 件</SummaryValue>
              </SummaryItem>
              <SummaryItem>
                <SummaryLabel>うち予定相殺額</SummaryLabel>
                <SummaryValue>
                  {(receivableSummary?.total?.pending_offset_amount ?? 0).toLocaleString()} 円
                </SummaryValue>
              </SummaryItem>
            </SummaryItems>
          </SummaryBanner>
          {missingBankAccountClearings.length > 0 && (
            <AttentionBanner>
              <span>
                振込先の口座情報が未登録の支払が {missingBankAccountClearings.length}{' '}
                件あります。このままでは振込データ（全銀ファイル）から除外されます。
              </span>
              <Button
                color="link"
                size="sm"
                onClick={() => {
                  // 絞り込みが変わると対象集合が変わるため選択をリセットする
                  // (一括精算は取り消せないため、見えていない行が選択に残らないようにする)。
                  setSelectedIds(new Set());
                  setOnlyMissingBankAccount((prev) => !prev);
                }}
              >
                {onlyMissingBankAccount ? 'すべて表示' : '該当だけ表示'}
              </Button>
            </AttentionBanner>
          )}
          <SectionWrapper>
            <HeadingWrapper>
              <Headings.h5>検索結果</Headings.h5>
            </HeadingWrapper>
            <ButtonsWrapper>
              <CSVButton
                fetchData={() => fetchClearingsForCSV()}
                fileName={generateCsvFileName()}
                headers={csvHeaders}
              />
              {/* 振込データの対象はサーバが検索条件から決めるため、画面の
                  「口座情報未登録のみ」表示には影響されない */}
              {isIssuer && (
                <Button onClick={() => setIsZenginModalOpen(true)}>振込データ</Button>
              )}
            </ButtonsWrapper>
          </SectionWrapper>
          {isIssuer && (
            <BulkBar>
              <BulkCount>
                {selectedIds.size} 件を選択中（未精算 全 {selectableCount} 件）
              </BulkCount>
              <ButtonsWrapper>
                {selectedIds.size > 0 && (
                  <Button
                    color="link"
                    size="sm"
                    disabled={isBulkProcessing}
                    onClick={() => setSelectedIds(new Set())}
                  >
                    選択をクリア
                  </Button>
                )}
                <Button
                  color="primary"
                  size="sm"
                  disabled={selectedIds.size === 0 || isBulkProcessing}
                  onClick={onClickBulkSettle}
                >
                  選択を精算済にする
                </Button>
              </ButtonsWrapper>
            </BulkBar>
          )}
          <ClearingListTable
            clearings={visibleClearings}
            selectedMoneyId={userPrivateMoney.id}
            onFinishClearing={onFinishClearing}
            selectable={isIssuer}
            selectedIds={selectedIds}
            onToggleSelect={onToggleSelect}
            onToggleSelectAll={onToggleSelectAll}
            allSelectableSelected={allSelectableSelected}
            selectableCount={selectableCount}
          />
          <ZenginExportModal
            isOpen={isZenginModalOpen}
            onClose={() => {
              setIsZenginModalOpen(false);
              // ダウンロードすると未精算のバッチが1件増えるので、その場で反映する。
              fetchUnsettledBatchCount();
            }}
            privateMoneys={allowedPrivateMoneys}
            defaultPrivateMoneyIds={userPrivateMoney.id ? [userPrivateMoney.id] : []}
            closingDateFrom={query.closing_date_from?.format()}
            closingDateTo={query.closing_date_to?.format()}
            claimedOrganizationCode={query.claimed_organization_code ?? undefined}
          />
        </>
      ) : (
        <UserPrivateMoneyNotAllowed />
      )}
    </PageWrapper>
  );
};

export default Clearings;
